import React from 'react';
import { DISABLED_PERFORMANCE_ROW_CLASS } from 'common/constants/valuations';
import { largeCurrencyFormat, oneDecimalPercentFormat, xStandardSuffixFormat } from 'common/formats/formats';
import { Cells, GridType } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  MULTIPLE_TYPES,
  PerformanceMetricsColumn,
} from 'pages/Valuations/approaches/guidelineCalibration/performance_metrics/config/createColumns/types';
import { SHEET_TITLES_CONSTANTS } from 'pages/Valuations/approaches/guidelineCalibration/performance_metrics/config/index';
import { SHEET_ALIASES_CONSTANTS } from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/common/constants/performanceMetrics';
import { PERFORMANCE_METRICS_SPREADSHEET_RANK_ALIAS } from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/common/constants/performanceMetrics/sheetAliases/sheetAliases';
import {
  CalibrationPerformanceRow,
  CellParserCalibrationParams,
  CustomParserParamCalibration,
  EvaluateNTMRevenueGrowthReturn,
  GetDisabledPerformanceRowClassParams,
  GetRankExpressionParams,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/config/customParser/types';
import { evaluateNTMRevenueGrowth } from 'pages/ValuationsAllocation/common/createPerformanceMetrics/helpers';
import { getExpression, getObjectValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { NON_POSITIVE_EXCLUDED_COLUMNS, ZERO_EXCLUDED_COLUMNS } from '../../../constants';

const {
  PERFORMANCE_METRICS_SPREADSHEET_COMPANY,
  PERFORMANCE_METRICS_SPREADSHEET_HEADER_SUBTITLE,
  PERFORMANCE_METRICS_SPREADSHEET_HEADER_TITLE,
} = SHEET_ALIASES_CONSTANTS;
const { CURRENCY_COLUMNS, PERFORMANCE_METRICS_SPREADSHEET_COLUMNS, PERFORMANCE_METRICS_SPREADSHEET_HEADERS } =
  SHEET_TITLES_CONSTANTS;

const currencyColumns = new Set(CURRENCY_COLUMNS);

function getRankExpression({
  colNumber,
  allComparisonsExpression,
  isMultiple,
  analyzedCompanyRow,
}: GetRankExpressionParams) {
  let expr = null;
  let hidden = true;
  if (allComparisonsExpression.length > 0 && colNumber < PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NET_INCOME) {
    // Percentile rank behaves slightly differently for multiples than the rest of the values as a business rule
    // 0 values in a row are indicative of a lack of data so we always filter those out, but the multiples
    // need to ignore the negative values as well.
    if (isMultiple) {
      expr = `=PERCENTRANK(FILTER_NON_POSITIVE_AS_OBJECT([${allComparisonsExpression}]), @${analyzedCompanyRow})`;
    } else {
      expr = `=PERCENTRANK(FILTER_ZEROS_AS_OBJECT([${allComparisonsExpression}]), @${analyzedCompanyRow})`;
    }
    hidden = false;
  }
  return { expr, hidden };
}

function CommonAliasMultiple(
  aliasId: string[],
  aliasMultiple: string,
  column: PerformanceMetricsColumn
): {
  customKey: string;
  gridType: GridType;
  format: {
    validateFloat: boolean;
    maximumFractionDigits: number;
    minimumFractionDigits: number;
    style: string;
    suffix: string;
  };
  alias: string;
  value: string | number | boolean | React.JSX.Element;
} {
  const newAliasMultiple: string = aliasMultiple + aliasId[1];
  const value = column[newAliasMultiple]?.value ?? '';
  const format = xStandardSuffixFormat;
  const gridType = 'number';
  const alias = newAliasMultiple;
  const customKey = newAliasMultiple;

  return { value, format, gridType, alias, customKey };
}

const getDisabledPerformanceRowClass = ({
  generatedCell,
  colNumber,
  isRowEnabled,
}: GetDisabledPerformanceRowClassParams): string => {
  if (isRowEnabled) {
    if (
      (ZERO_EXCLUDED_COLUMNS.includes(colNumber) && Number(generatedCell.value) === 0) ||
      (NON_POSITIVE_EXCLUDED_COLUMNS.includes(colNumber) && Number(generatedCell.value) <= 0)
    ) {
      return DISABLED_PERFORMANCE_ROW_CLASS;
    }
  }
  return '';
};

const cellParser = (params: CellParserCalibrationParams) => {
  const { calibration, alphabet, colIndex, column, row, rowIndex, allComparisonsExpression } = params;
  const { panel_id: panelId } = calibration;
  const updatedPanelId = panelId.replaceAll('-', '_');
  const rowNumber = rowIndex + 1;
  const colNumber = colIndex + 1;
  const columnLegend = alphabet[colIndex];
  const key = columnLegend + rowNumber;

  let { alias, expr = '', format = null, gridType = 'string', hidden } = getObjectValue(row);
  const { className = '', colSpan = 1, readOnly, options } = getObjectValue(row);
  const analyzedCompanyRow = (calibration?.performances ?? []).length + 2;

  let value = column[alias]?.value ?? '';
  value = value === 'N/A' ? 0 : value;

  let NTMRevenueGrowthEvaluation: EvaluateNTMRevenueGrowthReturn = {};
  const aliasId = alias.split('performance_metrics_');
  let aliasMultiple = '';
  let customKey = '';
  // Parse cell based on Row alias
  switch (alias) {
    // Header titles
    case PERFORMANCE_METRICS_SPREADSHEET_HEADER_TITLE:
      value = PERFORMANCE_METRICS_SPREADSHEET_HEADERS[colNumber].value;
      break;

    // Header subtitles
    case PERFORMANCE_METRICS_SPREADSHEET_HEADER_SUBTITLE:
      value = PERFORMANCE_METRICS_SPREADSHEET_HEADERS[colNumber].subtitle;
      break;

    case PERFORMANCE_METRICS_SPREADSHEET_RANK_ALIAS:
      {
        const rankValues = getRankExpression({
          colNumber,
          allComparisonsExpression,
          isMultiple: MULTIPLE_TYPES.includes(Object.keys(column)[0]),
          analyzedCompanyRow,
        });
        expr = rankValues.expr;
        hidden = rankValues.hidden;
        gridType = 'percentage';
      }
      break;

    // Display (for the moment) values on Company Volatility columns
    // Rest of the rows (excluding Header titles and subtitles)
    default:
      if (currencyColumns.has(colNumber as (typeof CURRENCY_COLUMNS)[number])) {
        format = largeCurrencyFormat;
        gridType = 'number';
      }
      // Parse cell based on Column number
      switch (colNumber) {
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.ENTERPRISE_VALUE:
          if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
            aliasId[1] = 'current_company';
            expr = `= calibration_inputs_${updatedPanelId}.enterprise_value_inputs `;
          }
          break;
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_EBITDA:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_EBITDA:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.TANGIBLE_BOOK:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NET_INCOME:
          format = largeCurrencyFormat;
          gridType = 'number';
          if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
            expr = '';
          }
          break;
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE_MULTIPLE:
          aliasMultiple = 'performance_metrics_ltm_revenue_multiple_';
          if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
            aliasId[1] = 'current_company';
            expr = `= calibration_inputs_${updatedPanelId}.enterprise_value_inputs / calibration_inputs_${updatedPanelId}.ltm_revenue_inputs`;
          }
          ({ value, format, gridType, alias, customKey } = CommonAliasMultiple(aliasId, aliasMultiple, column));
          break;
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE_MULTIPLE:
          aliasMultiple = 'performance_metrics_ntm_revenue_multiple_';
          if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
            aliasId[1] = 'current_company';
            expr = `= calibration_inputs_${updatedPanelId}.enterprise_value_inputs / calibration_inputs_${updatedPanelId}.ntm_revenue_inputs`;
          }
          ({ value, format, gridType, alias, customKey } = CommonAliasMultiple(aliasId, aliasMultiple, column));
          break;
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_EBITDA_MULTIPLE:
          aliasMultiple = 'performance_metrics_ltm_ebitda_multiple_';
          if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
            aliasId[1] = 'current_company';
            expr = `= calibration_inputs_${updatedPanelId}.enterprise_value_inputs / calibration_inputs_${updatedPanelId}.ltm_ebitda_inputs`;
          }
          ({ value, format, gridType, alias, customKey } = CommonAliasMultiple(aliasId, aliasMultiple, column));
          break;
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_EBITDA_MULTIPLE:
          aliasMultiple = 'performance_metrics_ntm_ebitda_multiple_';
          if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
            aliasId[1] = 'current_company';
            expr = `= calibration_inputs_${updatedPanelId}.enterprise_value_inputs / calibration_inputs_${updatedPanelId}.ntm_ebitda_inputs`;
          }
          ({ value, format, gridType, alias, customKey } = CommonAliasMultiple(aliasId, aliasMultiple, column));
          break;
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE_GROWTH:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.GROSS_MARGIN:
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.EBITDA_MARGIN:
          format = oneDecimalPercentFormat;
          gridType = 'percentage';
          break;
        case PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE_GROWTH:
          format = oneDecimalPercentFormat;
          gridType = 'percentage';
          NTMRevenueGrowthEvaluation = evaluateNTMRevenueGrowth({
            alphabet,
            alias,
            expr,
            rowNumber,
            value: value as string | number | null,
          });
          expr = NTMRevenueGrowthEvaluation?.expr ?? expr;
          value = NTMRevenueGrowthEvaluation?.value ?? value;
          break;
        default:
          break;
      }
      break;
  }
  const generatedCell = {
    ...row,
    alias,
    className,
    colSpan,
    columnLegend,
    expr: getExpression({ expr, columnLegend }),
    format,
    gridType,
    key,
    customKey,
    hidden,
    value,
    readOnly,
    options,
  } as ICell;
  if (row.isComparisonRow) {
    generatedCell.dataEditor = null;
    generatedCell.valueViewer = null;
    generatedCell.isEditableTitleCell = false;
    generatedCell.className += getDisabledPerformanceRowClass({
      generatedCell,
      colNumber,
      isRowEnabled: row.enabled ?? false,
    });
  }
  return {
    [key]: generatedCell,
  };
};

const customParser = (params: CustomParserParamCalibration) => {
  const { columns, rowConfig, tableData } = params;
  const { isDisabled, calibration } = tableData;

  let cells = {} as Cells;
  const alphabet = alphabetGenerator([], columns.length) as string[];

  const enabledComparisonsExpression = (rowConfig as CalibrationPerformanceRow[])
    .map((row, index) => (row.enabled && row.isComparisonRow ? `@${index + 1}` : undefined))
    .filter(label => label !== undefined);

  rowConfig.forEach((row, rowIndex: number) => {
    columns.forEach((column, colIndex: number) => {
      cells = {
        ...cells,
        ...cellParser({
          calibration,
          allComparisonsExpression: enabledComparisonsExpression,
          alphabet,
          colIndex,
          column,
          row,
          rowIndex,
          isDisabled,
          initialObject: params.initialObject,
        }),
      };
    });
  });

  return cells;
};

export default customParser;
